import React, { useId } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  MATERIAL_OPTIONS,
  RENDER_OUTPUT_MODE_OPTIONS,
  SINGLE_SIDED_OPTIONS,
  SMALL_DETAIL_ACTION_OPTIONS,
  SMALL_DETAIL_THRESHOLD_MODE_OPTIONS,
  TRANSFORMATION_PROFILES,
} from './converter-transformation-settings';

const fullRowSx = { gridColumn: '1 / -1' };

const fieldsetSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  m: 0,
  minWidth: 0,
  p: 2,
};

const fieldsGridSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: 'repeat(2, minmax(0, 1fr))',
  },
};

const SettingsFieldset = ({ children, legend }) => (
  <Box component="fieldset" sx={fieldsetSx}>
    <Typography
      component="legend"
      sx={{ color: 'text.primary', fontWeight: 600, px: 0.75 }}
      variant="subtitle2"
    >
      {legend}
    </Typography>
    <Box sx={fieldsGridSx}>
      {children}
    </Box>
  </Box>
);

const SettingsSwitch = ({
  checked,
  disabled,
  error,
  fullRow = true,
  helperText,
  id,
  label,
  onChange,
}) => {
  const helperId = `${id}-helper-text`;

  return (
    <FormControl
      disabled={disabled}
      error={Boolean(error)}
      sx={fullRow ? fullRowSx : undefined}
    >
      <FormControlLabel
        control={(
          <Switch
            checked={Boolean(checked)}
            disabled={disabled}
            id={id}
            inputProps={{
              'aria-describedby': error || helperText ? helperId : undefined,
            }}
            onChange={(event) => onChange(event.target.checked)}
          />
        )}
        label={label}
        sx={{ m: 0 }}
      />
      {(error || helperText) && (
        <FormHelperText id={helperId} sx={{ ml: 0 }}>
          {error || helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

const SettingsSelect = ({
  disabled,
  error,
  fullRow = false,
  id,
  label,
  onChange,
  options,
  translationGroup,
  value,
  t,
}) => {
  const helperId = `${id}-helper-text`;
  const labelId = `${id}-label`;

  return (
    <FormControl
      disabled={disabled}
      error={Boolean(error)}
      fullWidth
      size="small"
      sx={fullRow ? fullRowSx : undefined}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        id={id}
        inputProps={{
          'aria-describedby': error ? helperId : undefined,
        }}
        label={label}
        labelId={labelId}
        onChange={(event) => onChange(event.target.value)}
        value={value ?? ''}
      >
        {options.map(({ value: optionValue }) => (
          <MenuItem key={optionValue} value={optionValue}>
            {t(
              `converterTransformation.options.${translationGroup}.${optionValue}`,
            )}
          </MenuItem>
        ))}
      </Select>
      {error && (
        <FormHelperText id={helperId}>{error}</FormHelperText>
      )}
    </FormControl>
  );
};

const ConverterTransformationSettingsForm = ({
  disabled = false,
  errors = {},
  onChange,
  profile,
  value,
}) => {
  const { t } = useTranslation();
  const componentId = useId();
  const isGlbImport = profile === TRANSFORMATION_PROFILES.GLB_IMPORT;
  const updateValue = (field, nextValue) => {
    onChange({
      ...value,
      [field]: nextValue,
    });
  };
  const fieldId = (field) => `${componentId}-${field}`;
  const heavyGeometryDisabled = disabled || !value.heavyGeometryEnabled;
  const smallDetailThresholdDisabled =
    disabled || value.smallDetailAction !== 'delete';
  const manualSmallDetailThresholdDisabled =
    smallDetailThresholdDisabled ||
    value.smallDetailThresholdMode !== 'manual';
  const textureResizeDisabled = disabled || !value.textureResizeEnabled;
  const physicalSizeCapDisabled =
    textureResizeDisabled || !value.physicalSizeCapEnabled;

  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      <SettingsFieldset
        legend={t('converterTransformation.sections.batching')}
      >
        <SettingsSwitch
          checked={isGlbImport ? false : value.batchingEnabled}
          disabled={disabled || isGlbImport}
          error={errors.batchingEnabled}
          helperText={
            isGlbImport
              ? t('converterTransformation.messages.batchingUnavailableForGlb')
              : undefined
          }
          id={fieldId('batchingEnabled')}
          label={t('converterTransformation.fields.batchingEnabled')}
          onChange={(nextValue) => updateValue('batchingEnabled', nextValue)}
        />
      </SettingsFieldset>

      <SettingsFieldset
        legend={t('converterTransformation.sections.geometry')}
      >
        <SettingsSwitch
          checked={value.heavyGeometryEnabled}
          disabled={disabled}
          error={errors.heavyGeometryEnabled}
          id={fieldId('heavyGeometryEnabled')}
          label={t('converterTransformation.fields.heavyGeometryEnabled')}
          onChange={(nextValue) => updateValue('heavyGeometryEnabled', nextValue)}
        />

        <TextField
          disabled={heavyGeometryDisabled}
          error={Boolean(errors.targetTrianglesPerSquareMeter)}
          fullWidth
          helperText={errors.targetTrianglesPerSquareMeter}
          id={fieldId('targetTrianglesPerSquareMeter')}
          inputProps={{ min: 1, step: 1 }}
          label={t(
            'converterTransformation.fields.targetTrianglesPerSquareMeter',
          )}
          name="targetTrianglesPerSquareMeter"
          onChange={(event) => {
            updateValue('targetTrianglesPerSquareMeter', event.target.value);
          }}
          size="small"
          type="number"
          value={value.targetTrianglesPerSquareMeter ?? ''}
        />

        <TextField
          disabled={heavyGeometryDisabled}
          error={Boolean(errors.maxError)}
          fullWidth
          helperText={errors.maxError}
          id={fieldId('maxError')}
          inputProps={{ min: 0, max: 1, step: 'any' }}
          label={t('converterTransformation.fields.maxError')}
          name="maxError"
          onChange={(event) => updateValue('maxError', event.target.value)}
          size="small"
          type="number"
          value={value.maxError ?? ''}
        />

        <SettingsSwitch
          checked={value.preserveBorders}
          disabled={heavyGeometryDisabled}
          error={errors.preserveBorders}
          id={fieldId('preserveBorders')}
          label={t('converterTransformation.fields.preserveBorders')}
          onChange={(nextValue) => updateValue('preserveBorders', nextValue)}
        />
      </SettingsFieldset>

      <SettingsFieldset
        legend={t('converterTransformation.sections.smallDetails')}
      >
        <SettingsSelect
          disabled={disabled}
          error={errors.smallDetailAction}
          id={fieldId('smallDetailAction')}
          label={t('converterTransformation.fields.smallDetailAction')}
          onChange={(nextValue) => updateValue('smallDetailAction', nextValue)}
          options={SMALL_DETAIL_ACTION_OPTIONS}
          t={t}
          translationGroup="smallDetailAction"
          value={value.smallDetailAction}
        />

        <SettingsSelect
          disabled={smallDetailThresholdDisabled}
          error={errors.smallDetailThresholdMode}
          id={fieldId('smallDetailThresholdMode')}
          label={t('converterTransformation.fields.smallDetailThresholdMode')}
          onChange={(nextValue) => {
            updateValue('smallDetailThresholdMode', nextValue);
          }}
          options={SMALL_DETAIL_THRESHOLD_MODE_OPTIONS}
          t={t}
          translationGroup="smallDetailThresholdMode"
          value={value.smallDetailThresholdMode}
        />

        <TextField
          disabled={manualSmallDetailThresholdDisabled}
          error={Boolean(errors.smallDetailThresholdMeters)}
          fullWidth
          helperText={errors.smallDetailThresholdMeters}
          id={fieldId('smallDetailThresholdMeters')}
          inputProps={{ min: 0, step: 'any' }}
          label={t('converterTransformation.fields.smallDetailThresholdMeters')}
          name="smallDetailThresholdMeters"
          onChange={(event) => {
            updateValue('smallDetailThresholdMeters', event.target.value);
          }}
          size="small"
          sx={fullRowSx}
          type="number"
          value={value.smallDetailThresholdMeters ?? ''}
        />
      </SettingsFieldset>

      <SettingsFieldset
        legend={t('converterTransformation.sections.materials')}
      >
        <SettingsSelect
          disabled={disabled}
          error={errors.targetMaterial}
          fullRow
          id={fieldId('targetMaterial')}
          label={t('converterTransformation.fields.targetMaterial')}
          onChange={(nextValue) => updateValue('targetMaterial', nextValue)}
          options={MATERIAL_OPTIONS}
          t={t}
          translationGroup="targetMaterial"
          value={value.targetMaterial}
        />

        <SettingsSelect
          disabled={disabled}
          error={errors.singleSidedMaterials}
          fullRow
          id={fieldId('singleSidedMaterials')}
          label={t('converterTransformation.fields.singleSidedMaterials')}
          onChange={(nextValue) => {
            updateValue('singleSidedMaterials', nextValue);
          }}
          options={SINGLE_SIDED_OPTIONS}
          t={t}
          translationGroup="singleSidedMaterials"
          value={value.singleSidedMaterials}
        />
      </SettingsFieldset>

      <SettingsFieldset
        legend={t('converterTransformation.sections.textures')}
      >
        <SettingsSwitch
          checked={value.textureResizeEnabled}
          disabled={disabled}
          error={errors.textureResizeEnabled}
          id={fieldId('textureResizeEnabled')}
          label={t('converterTransformation.fields.textureResizeEnabled')}
          onChange={(nextValue) => updateValue('textureResizeEnabled', nextValue)}
        />

        <TextField
          disabled={textureResizeDisabled}
          error={Boolean(errors.maxDimension)}
          fullWidth
          helperText={errors.maxDimension}
          id={fieldId('maxDimension')}
          inputProps={{ min: 1, max: 4096, step: 1 }}
          label={t('converterTransformation.fields.maxDimension')}
          name="maxDimension"
          onChange={(event) => updateValue('maxDimension', event.target.value)}
          size="small"
          sx={fullRowSx}
          type="number"
          value={value.maxDimension ?? ''}
        />

        <SettingsSwitch
          checked={value.physicalSizeCapEnabled}
          disabled={textureResizeDisabled}
          error={errors.physicalSizeCapEnabled}
          id={fieldId('physicalSizeCapEnabled')}
          label={t('converterTransformation.fields.physicalSizeCapEnabled')}
          onChange={(nextValue) => {
            updateValue('physicalSizeCapEnabled', nextValue);
          }}
        />

        <TextField
          disabled={physicalSizeCapDisabled}
          error={Boolean(errors.minDimension)}
          fullWidth
          helperText={errors.minDimension}
          id={fieldId('minDimension')}
          inputProps={{ min: 1, max: 4096, step: 1 }}
          label={t('converterTransformation.fields.minDimension')}
          name="minDimension"
          onChange={(event) => updateValue('minDimension', event.target.value)}
          size="small"
          type="number"
          value={value.minDimension ?? ''}
        />

        <TextField
          disabled={physicalSizeCapDisabled}
          error={Boolean(errors.pixelsPerMeter)}
          fullWidth
          helperText={errors.pixelsPerMeter}
          id={fieldId('pixelsPerMeter')}
          inputProps={{ min: 0, step: 'any' }}
          label={t('converterTransformation.fields.pixelsPerMeter')}
          name="pixelsPerMeter"
          onChange={(event) => updateValue('pixelsPerMeter', event.target.value)}
          size="small"
          type="number"
          value={value.pixelsPerMeter ?? ''}
        />
      </SettingsFieldset>

      <SettingsFieldset
        legend={t('converterTransformation.sections.compression')}
      >
        <SettingsSwitch
          checked={value.ktx2Enabled}
          disabled={disabled}
          error={errors.ktx2Enabled}
          helperText={t('converterTransformation.messages.ktx2Experimental')}
          id={fieldId('ktx2Enabled')}
          label={t('converterTransformation.fields.ktx2Enabled')}
          onChange={(nextValue) => updateValue('ktx2Enabled', nextValue)}
        />
      </SettingsFieldset>

      {isGlbImport && (
        <SettingsFieldset
          legend={t('converterTransformation.sections.preview')}
        >
          <SettingsSelect
            disabled={disabled}
            error={errors.renderOutputMode}
            fullRow
            id={fieldId('renderOutputMode')}
            label={t('converterTransformation.fields.renderOutputMode')}
            onChange={(nextValue) => updateValue('renderOutputMode', nextValue)}
            options={RENDER_OUTPUT_MODE_OPTIONS}
            t={t}
            translationGroup="renderOutputMode"
            value={value.renderOutputMode}
          />

          <Box
            sx={{
              alignItems: 'center',
              color: 'text.secondary',
              display: 'flex',
              gap: 0.75,
              ...fullRowSx,
            }}
          >
            <InfoOutlinedIcon aria-hidden fontSize="small" />
            <Typography variant="caption">
              {t('converterTransformation.messages.previewAlwaysGenerated')}
            </Typography>
          </Box>
        </SettingsFieldset>
      )}

      <SettingsSwitch
        checked={value.automaticOptimizationsEnabled ?? true}
        disabled={disabled}
        error={errors.automaticOptimizationsEnabled}
        id={fieldId('automaticOptimizationsEnabled')}
        label={t(
          'converterTransformation.fields.automaticOptimizationsEnabled',
        )}
        onChange={(nextValue) => {
          updateValue('automaticOptimizationsEnabled', nextValue);
        }}
      />
    </Box>
  );
};

export default ConverterTransformationSettingsForm;
